import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Tag, Share2, ArrowRight, List, CheckCircle2 } from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { SEO } from '../components/common';
import Button from '../components/ui/Button';
import BlogCard from '../components/ui/BlogCard';
import {
  getPostBySlug,
  getRecentPosts,
  BLOG_TAGS,
  formatBlogDate,
} from '../data/blogPosts';

/* ── Extract H2 headings from markdown for Table of Contents ── */
const extractHeadings = (markdown) => {
  const headingRegex = /^## (.+)$/gm;
  const headings = [];
  let match;
  while ((match = headingRegex.exec(markdown)) !== null) {
    const text = match[1].replace(/\*\*/g, '').replace(/`/g, '').trim();
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    headings.push({ text, id });
  }
  return headings;
};

/* ── Custom Markdown Components ── */
const markdownComponents = {
  h2: ({ children, ...props }) => {
    const text = typeof children === 'string' ? children : 
      Array.isArray(children) ? children.map(c => typeof c === 'string' ? c : c?.props?.children || '').join('') : '';
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return (
      <h2 id={id} className="group scroll-mt-24" {...props}>
        <span className="relative">
          {children}
          <a href={`#${id}`} className="absolute -left-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-primary-400 transition-opacity" aria-label="Link to section">
            #
          </a>
        </span>
      </h2>
    );
  },
  h3: ({ children, ...props }) => (
    <h3 {...props}>
      {children}
    </h3>
  ),
  table: ({ children }) => (
    <div className="article-table-wrapper my-8 overflow-x-auto rounded-xl border border-neutral-200 shadow-sm">
      <table className="article-table w-full">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-primary-600 text-white">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="px-4 py-3 text-left text-sm font-semibold tracking-wide">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-3 text-sm text-neutral-700 border-t border-neutral-100">{children}</td>
  ),
  tr: ({ children, ...props }) => (
    <tr className="even:bg-neutral-50 hover:bg-primary-50/40 transition-colors" {...props}>{children}</tr>
  ),
  blockquote: ({ children }) => (
    <div className="article-callout my-8 flex gap-4 rounded-xl border-l-4 border-primary-500 bg-gradient-to-r from-primary-50 to-primary-50/30 p-5 shadow-sm">
      <div className="mt-0.5 flex-shrink-0">
        <CheckCircle2 className="w-5 h-5 text-primary-600" />
      </div>
      <div className="text-neutral-700 [&>p]:m-0 [&>p]:leading-relaxed">{children}</div>
    </div>
  ),
  ul: ({ children }) => (
    <ul className="article-list space-y-2 my-6">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="article-list-ordered space-y-2 my-6">{children}</ol>
  ),
  li: ({ children, ordered, ...props }) => (
    <li className="flex gap-2 text-neutral-700 leading-relaxed" {...props}>
      <span className="mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-primary-500" />
      <span>{children}</span>
    </li>
  ),
  a: ({ href, children }) => (
    <a href={href} className="text-primary-600 font-medium underline decoration-primary-300 decoration-2 underline-offset-2 hover:decoration-primary-500 transition-colors" target={href?.startsWith('http') ? '_blank' : undefined} rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}>
      {children}
    </a>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-neutral-900">{children}</strong>
  ),
  img: ({ src, alt }) => (
    <figure className="my-8">
      <div className="overflow-hidden rounded-xl shadow-md">
        <img src={src} alt={alt || ''} className="w-full object-cover" loading="lazy" />
      </div>
      {alt && <figcaption className="mt-3 text-center text-sm text-neutral-500 italic">{alt}</figcaption>}
    </figure>
  ),
  hr: () => (
    <div className="my-10 flex items-center gap-4">
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-neutral-300 to-transparent" />
      <div className="flex gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-primary-400" />
        <span className="w-1.5 h-1.5 rounded-full bg-primary-300" />
        <span className="w-1.5 h-1.5 rounded-full bg-primary-200" />
      </div>
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-neutral-300 to-transparent" />
    </div>
  ),
};

/* ── Reading Progress Bar ── */
const ReadingProgress = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? Math.min((scrollTop / docHeight) * 100, 100) : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-neutral-100">
      <div
        className="h-full bg-gradient-to-r from-primary-500 to-primary-400 transition-[width] duration-150 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

/* ── Table of Contents Sidebar ── */
const TableOfContents = ({ headings }) => {
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
    );

    headings.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [headings]);

  if (headings.length < 2) return null;

  return (
    <nav className="hidden xl:block sticky top-24 self-start w-64 flex-shrink-0">
      <div className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 text-sm font-semibold text-neutral-900 mb-4">
          <List className="w-4 h-4 text-primary-500" />
          Table of Contents
        </div>
        <ul className="space-y-1 text-sm scrollbar-thin max-h-[60vh] overflow-y-auto pr-1">
          {headings.map(({ text, id }) => (
            <li key={id}>
              <a
                href={`#${id}`}
                className={`block py-1.5 px-3 rounded-lg transition-all duration-200 leading-snug ${
                  activeId === id
                    ? 'bg-primary-50 text-primary-700 font-medium border-l-2 border-primary-500'
                    : 'text-neutral-500 hover:text-neutral-800 hover:bg-neutral-50'
                }`}
              >
                {text}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

/* ── Main Blog Post Page ── */
const BlogPostPage = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  const headings = useMemo(() => (post ? extractHeadings(post.content) : []), [post]);

  const relatedPosts = useMemo(() => {
    if (!post) return [];
    const related = getRecentPosts(10)
      .filter((p) => p.id !== post.id && p.tags.some((t) => post.tags.includes(t)))
      .slice(0, 3);
    return related.length >= 2
      ? related
      : getRecentPosts(4).filter((p) => p.id !== post.id).slice(0, 3);
  }, [post]);

  const handleShare = useCallback(async () => {
    if (!post) return;
    const url = `${window.location.origin}/blog/${post.slug}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: post.title, text: post.description, url });
      } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
    }
  }, [post]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Blog', url: '/blog' },
    { name: post.title, url: `/blog/${post.slug}` },
  ];

  return (
    <>
      <ReadingProgress />

      <SEO
        title={post.title}
        description={post.description}
        keywords={post.tags.map((t) => BLOG_TAGS[t]?.label || t).join(', ') + ', MRI Kasur, diagnostic center'}
        url={`/blog/${post.slug}`}
        type="article"
        image={post.image}
        breadcrumbs={breadcrumbs}
        article={{
          publishedTime: post.date,
          modifiedTime: post.date,
          author: post.author?.name || 'MTDC Team',
        }}
      />

      {/* ── Article Hero ── */}
      <section className="relative bg-gradient-to-br from-primary-900 via-primary-800 to-primary-950 py-16 md:py-24 overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link
              to="/blog"
              className="inline-flex items-center text-primary-300 hover:text-white mb-8 transition-colors text-sm group"
            >
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
              Back to Blog
            </Link>

            <div className="flex flex-wrap gap-2 mb-5">
              {post.tags.map((tag) => {
                const tagInfo = BLOG_TAGS[tag];
                return tagInfo ? (
                  <Link
                    key={tag}
                    to={`/blog/tags/${tag}`}
                    className="text-xs font-medium px-3 py-1 rounded-full bg-white/15 text-white backdrop-blur-sm hover:bg-white/25 transition-colors"
                  >
                    {tagInfo.label}
                  </Link>
                ) : null;
              })}
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight font-heading">
              {post.title}
            </h1>

            <p className="text-primary-200 text-lg mb-8 max-w-3xl leading-relaxed">
              {post.description}
            </p>

            <div className="flex flex-wrap items-center gap-6 text-primary-300">
              <div className="flex items-center gap-3">
                {post.author?.avatar ? (
                  <img src={post.author.avatar} alt="" className="w-11 h-11 rounded-full ring-2 ring-primary-400/50 object-cover" loading="lazy" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-primary-700 ring-2 ring-primary-400/50 flex items-center justify-center">
                    <span className="text-white font-semibold">
                      {post.author?.name?.charAt(0) || 'M'}
                    </span>
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-white">{post.author?.name}</p>
                  <p className="text-xs text-primary-400">{post.author?.title}</p>
                </div>
              </div>

              <span className="hidden md:block w-px h-6 bg-primary-600" />

              <div className="flex items-center gap-1.5 text-sm">
                <Calendar className="w-4 h-4" />
                <time dateTime={post.date}>{formatBlogDate(post.date)}</time>
              </div>

              <div className="flex items-center gap-1.5 text-sm">
                <Clock className="w-4 h-4" />
                <span>{post.readingTime} min read</span>
              </div>

              <button
                onClick={handleShare}
                className="flex items-center gap-1.5 text-sm hover:text-white transition-colors ml-auto bg-white/10 px-4 py-2 rounded-full hover:bg-white/20"
                title="Share this article"
              >
                <Share2 className="w-4 h-4" />
                <span>Share</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Featured Image (overlapping hero) ── */}
      {post.image && (
        <div className="container-custom -mt-8 mb-10 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-72 md:h-[28rem] object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Article Content + TOC ── */}
      <article className="py-10 md:py-16 bg-white">
        <div className="container-custom">
          <div className="max-w-7xl mx-auto flex gap-12">
            {/* Main content */}
            <div className="min-w-0 flex-1 max-w-4xl mx-auto xl:mx-0">
              <div className="article-content prose prose-lg max-w-none
                prose-headings:font-heading prose-headings:text-neutral-900 prose-headings:font-bold
                prose-h2:text-2xl prose-h2:md:text-[1.75rem] prose-h2:mt-12 prose-h2:mb-5 prose-h2:pb-3 prose-h2:border-b-2 prose-h2:border-primary-100
                prose-h3:text-xl prose-h3:mt-9 prose-h3:mb-4 prose-h3:text-primary-800
                prose-p:text-neutral-700 prose-p:leading-[1.85] prose-p:mb-5 prose-p:text-[1.0625rem]
                prose-a:text-primary-600 prose-a:no-underline
                prose-strong:text-neutral-900 prose-strong:font-semibold
                prose-img:rounded-xl prose-img:shadow-lg
              ">
                <Markdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={markdownComponents}
                >
                  {post.content}
                </Markdown>
              </div>

              {/* ── Article Footer ── */}
              <div className="mt-14 pt-8 border-t-2 border-neutral-100">
                {/* Author card */}
                <div className="flex items-start gap-4 bg-neutral-50 rounded-xl p-6 mb-8">
                  {post.author?.avatar ? (
                    <img src={post.author.avatar} alt="" className="w-14 h-14 rounded-full object-cover ring-2 ring-primary-200" loading="lazy" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center ring-2 ring-primary-200 flex-shrink-0">
                      <span className="text-primary-600 font-bold text-lg">
                        {post.author?.name?.charAt(0) || 'M'}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-neutral-900">{post.author?.name}</p>
                    <p className="text-sm text-neutral-500 mb-2">{post.author?.title}</p>
                    <p className="text-sm text-neutral-600 leading-relaxed">
                      Providing expert diagnostic imaging insights to help patients make informed healthcare decisions.
                    </p>
                  </div>
                </div>

                {/* Tags */}
                <div className="flex items-center gap-3 flex-wrap mb-10">
                  <Tag className="w-4 h-4 text-neutral-400" />
                  {post.tags.map((tag) => {
                    const tagInfo = BLOG_TAGS[tag];
                    return tagInfo ? (
                      <Link
                        key={tag}
                        to={`/blog/tags/${tag}`}
                        className={`text-sm font-medium px-4 py-2 rounded-full transition-shadow hover:shadow-md ${tagInfo.color}`}
                      >
                        {tagInfo.label}
                      </Link>
                    ) : null;
                  })}
                </div>

                {/* CTA */}
                <div className="relative overflow-hidden bg-gradient-to-br from-primary-700 via-primary-600 to-primary-800 rounded-2xl p-10 text-white text-center shadow-xl">
                  <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '30px 30px' }} />
                  <div className="relative z-10">
                    <h3 className="text-2xl md:text-3xl font-bold mb-3 font-heading">Ready to Book Your Scan?</h3>
                    <p className="text-primary-100 mb-8 max-w-lg mx-auto text-lg leading-relaxed">
                      Schedule your appointment at Mian Tufail Diagnostic Center for accurate 
                      diagnosis using the latest Siemens technology.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                      <Link to="/book-appointment">
                        <Button variant="accent" icon={ArrowRight} iconPosition="right">
                          Book Appointment
                        </Button>
                      </Link>
                      <a href="tel:+923000478311">
                        <Button variant="secondary">
                          Call +92 300 047 8311
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Table of Contents sidebar */}
            <TableOfContents headings={headings} />
          </div>
        </div>
      </article>

      {/* ── Related Posts ── */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-neutral-50">
          <div className="container-custom">
            <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-10 text-center font-heading">
              Related <span className="gradient-text">Articles</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {relatedPosts.map((relatedPost) => (
                <BlogCard key={relatedPost.id} post={relatedPost} />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default BlogPostPage;
